<script setup lang="ts">
import { ref, toRef } from "vue"
import { useFloating, offset, flip, shift, autoUpdate } from "@floating-ui/vue"
import type { EventDTO } from "@/js/types/generated"
import CategoryBadge from "@/js/components/CategoryBadge.vue"
import { formatDate } from "@/js/utils/formatDate"
import { getEventMetadataIconComponent } from "@/js/utils/eventMetadataIcons"

const props = defineProps<{
	event: EventDTO
	referenceEl: HTMLElement
}>()

const tooltipFloating = ref<HTMLElement | null>(null)

const { floatingStyles } = useFloating(toRef(props, "referenceEl"), tooltipFloating, {
	placement: "top",
	middleware: [offset(8), flip(), shift({ padding: 8 })],
	whileElementsMounted: autoUpdate,
})
</script>

<template>
	<div
		ref="tooltipFloating"
		:style="floatingStyles"
		class="z-50 max-w-xs rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
	>
		<div v-if="event.thumbnail" class="mb-2 overflow-hidden rounded">
			<img :src="event.thumbnail" :alt="event.title" class="h-24 w-full object-cover" />
		</div>
		<div class="text-sm font-semibold text-gray-900">{{ event.title }}</div>
		<div v-if="event.deck" class="mt-1 text-xs font-medium text-gray-700">{{ event.deck }}</div>
		<div class="mt-1 text-xs text-gray-500">{{ formatDate(event.start, event.end, event.allDay) }}</div>
		<div
			v-for="item in event.metadata"
			:key="item.label + item.value"
			class="mt-1 flex items-center gap-1 text-xs text-gray-500"
		>
			<component :is="getEventMetadataIconComponent(item.icon)" class="size-3" :aria-label="item.label" />
			{{ item.value }}
		</div>
		<CategoryBadge v-if="event.category" :name="event.category" :color="event.color" class="mt-1" />
		<div v-if="event.excerpt" class="mt-2 text-xs text-gray-600 line-clamp-3">
			{{ event.excerpt }}
		</div>
	</div>
</template>
